import { IMAGE_CONFIG } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { prefersReducedMotion, provideMotionAwareAnimations } from '@infinitform/shared-ui';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { Chart } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';

// Register chart plugins globally
Chart.register(zoomPlugin, MatrixController, MatrixElement);

// Charts draw without animation under reduced motion.
if (prefersReducedMotion()) {
  Chart.defaults.set({ animation: false });
}

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { apiInterceptor } from '@infinitform/cabinet-features/interceptors/api.interceptor';
import { jwtInterceptor } from '@infinitform/cabinet-features/interceptors/jwt.interceptor';
import { errorInterceptor } from '@infinitform/cabinet-features/interceptors/error.interceptor';
import { PageTitleStrategy } from '@infinitform/cabinet-features/services/page-title.strategy';
import { AppInitializationService } from '@infinitform/cabinet-features/services/security/app-initialization.service';
import { configureCabinetEnvironment } from '@infinitform/cabinet-features/config/cabinet-environment';
import { PROJECT_EXPORT_IMPORT_SERVICE, AUTO_ORIENT_SERVICE, FILE_SAVE_SERVICE } from '@infinitform/platform-core';
import { ProjectExportImportCloudService, CLOUD_PLATFORM_CONFIG, CloudBdfService, CloudSoftjawsService, CloudAutoOrientService, UserSettingsCloudService, FileSaveCloudService } from '@infinitform/platform-cloud';
import { BDF_SERVICE, SOFTJAWS_SERVICE, USER_SETTINGS_SERVICE } from '@infinitform/platform-core';
import { environment } from './environments/environment';
import { OpenProjectsService } from '@infinitform/cabinet-features/open-projects/open-projects.service';
import { OPEN_PROJECTS } from '@infinitform/cabinet-features/open-projects/open-projects.tokens';
import { bootstrapApplication } from '@angular/platform-browser';

// Configure cabinet environment from Angular environment file
// (fileReplacements in angular.json swaps this for dev/staging builds)
configureCabinetEnvironment({
  apiAddress: environment.API_ADDRESS,
  socketAddress: environment.SOCKET_ADDRESS,
  frontendAddress: environment.FRONTEND_ADDRESS,
  editorAddress: environment.EDITOR_ADDRESS,
  production: environment.PRODUCTION,
  apiStorageMode: environment.API_STORAGE_MODE as 's3' | 'local',
  subscriptionsEnabled: environment.SUBSCRIPTIONS_ENABLED,
  envLabel: environment.ENV_LABEL,
});

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([apiInterceptor, jwtInterceptor, errorInterceptor])),
    provideAppInitializer(() => inject(AppInitializationService).initializeApp()),
    {
      provide: IMAGE_CONFIG,
      useValue: { placeholderResolution: 40 }
    },
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    { provide: CLOUD_PLATFORM_CONFIG, useValue: { apiBaseUrl: environment.API_ADDRESS } },
    { provide: PROJECT_EXPORT_IMPORT_SERVICE, useClass: ProjectExportImportCloudService },
    { provide: BDF_SERVICE, useClass: CloudBdfService },
    { provide: SOFTJAWS_SERVICE, useClass: CloudSoftjawsService },
    { provide: AUTO_ORIENT_SERVICE, useClass: CloudAutoOrientService },
    { provide: USER_SETTINGS_SERVICE, useClass: UserSettingsCloudService },
    { provide: FILE_SAVE_SERVICE, useClass: FileSaveCloudService },
    OpenProjectsService,
    { provide: OPEN_PROJECTS, useExisting: OpenProjectsService },
    provideMotionAwareAnimations(),
    provideCharts(withDefaultRegisterables())
  ]
}).catch((err) => console.error(err));
